import { useEffect, useRef } from 'react';
import { streamApi } from '../services/api';

/**
 * Play a camera over WebRTC into a <video> element.
 *
 * The offer goes to our own backend, which authorizes it and relays to go2rtc; media then
 * flows straight from go2rtc to the browser with no transcode. See routes/streams.js.
 *
 * Non-trickle by design: go2rtc blocks until its ICE gathering finishes and returns a
 * complete answer with every candidate inline, so one HTTP round trip is all that's
 * needed. `iceServers: []` keeps the browser's own gathering to local candidates, which
 * completes in milliseconds — we're connecting to a server with a public address, so
 * STUN would only add latency.
 *
 * Calls onFailed() for anything that means "this isn't going to work" so the caller can
 * drop to another player rather than sit on a black frame.
 */
export function useWebRtcStream({ enabled, cameraId, videoEl, onReady, onFailed }) {
  // Keep callbacks in refs so a re-render doesn't tear down a working connection.
  const onReadyRef = useRef(onReady);
  const onFailedRef = useRef(onFailed);
  onReadyRef.current = onReady;
  onFailedRef.current = onFailed;

  useEffect(() => {
    if (!enabled || !cameraId || !videoEl) return;

    let pc = null;
    let cancelled = false;
    let watchdog = null;

    const fail = (reason) => {
      if (cancelled) return;
      cancelled = true;
      clearTimeout(watchdog);
      onFailedRef.current?.(reason);
    };

    (async () => {
      try {
        pc = new RTCPeerConnection({ iceServers: [], bundlePolicy: 'max-bundle' });
        pc.addTransceiver('video', { direction: 'recvonly' });

        pc.ontrack = (e) => {
          if (cancelled) return;
          videoEl.srcObject = e.streams[0];
          // Autoplay can still be refused; muted + playsInline is set on the element.
          videoEl.play?.().catch(() => {});
        };

        pc.onconnectionstatechange = () => {
          if (!pc) return;
          if (pc.connectionState === 'failed') fail('ice-failed');
        };

        // The camera's keyframe interval is 2-4s, so first frame can legitimately take
        // that long. Allow generous headroom before declaring failure — a tighter
        // watchdog would demote the slowest camera on every single connect.
        watchdog = setTimeout(() => fail('timeout'), 12000);

        await pc.setLocalDescription(await pc.createOffer());
        await waitForIceGathering(pc, 2000);
        if (cancelled) return;

        const { data } = await streamApi.webrtcOffer(cameraId, pc.localDescription.sdp);
        if (cancelled) return;
        await pc.setRemoteDescription({ type: 'answer', sdp: data.sdp });

        const ready = () => {
          if (cancelled) return;
          clearTimeout(watchdog);
          onReadyRef.current?.();
        };
        // 'playing' is the honest signal that frames are rendering; 'canplay' can fire
        // before anything is on screen with a live MediaStream.
        videoEl.addEventListener('playing', ready, { once: true });
      } catch (err) {
        // The backend labels unrecoverable cases (422 codec mismatch, 503 go2rtc down).
        fail(err?.response?.data?.fallback ? `backend:${err.response.status}` : (err?.message || 'offer-failed'));
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(watchdog);
      try { pc?.getReceivers?.().forEach(r => r.track?.stop()); } catch (_) {}
      try { pc?.close(); } catch (_) {}
      // Closing the peer connection is what makes go2rtc drop this consumer and, when
      // it's the last one, stop pulling RTSP from the camera.
      if (videoEl) videoEl.srcObject = null;
    };
  }, [enabled, cameraId, videoEl]);
}

// Resolve once ICE gathering completes, or after `timeoutMs` with whatever we have.
function waitForIceGathering(pc, timeoutMs) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => { clearTimeout(t); pc.removeEventListener('icegatheringstatechange', check); resolve(); };
    const check = () => { if (pc.iceGatheringState === 'complete') done(); };
    const t = setTimeout(done, timeoutMs);
    pc.addEventListener('icegatheringstatechange', check);
  });
}
